import { Avatar } from '../../../shared/ui/avatar';
import { KeyboardIcon, ProfileIcon, SignOutIcon } from '../../../shared/ui/icons';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '../../../shared/ui/menu';
import { Segmented } from '../../../shared/ui/segmented';
import { ShortcutTooltip } from '../../../shared/ui/tooltip';
import { RAIL_ITEM, SHELL_CHORDS } from './nav';

export type TTheme = 'system' | 'dark' | 'light';

const THEMES: { value: TTheme; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'dark', label: 'Dark' },
  { value: 'light', label: 'Light' },
];

export interface IUserMenuProps {
  name: string;
  email: string;
  /** The zone every time on screen is shown in, from Account preferences. */
  timeZone: string;
  theme: TTheme;
  onTheme: (theme: TTheme) => void;
  onAccount: () => void;
  onShortcuts: () => void;
  onSignOut: () => void;
}

const ITEM_KEY = 'ml-auto font-mono text-micro text-muted';

export const UserMenu = ({
  name,
  email,
  timeZone,
  theme,
  onTheme,
  onAccount,
  onShortcuts,
  onSignOut,
  open,
  onOpenChange,
  collapsed,
}: IUserMenuProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collapsed: boolean;
}) => (
  <Menu open={open} onOpenChange={onOpenChange}>
    <ShortcutTooltip off={!collapsed} label={name} keys={SHELL_CHORDS.account.keys}>
      <MenuTrigger aria-label={`${name}, account menu`} className={RAIL_ITEM}>
        <Avatar name={name} className="-mx-hair size-rail-avatar text-caps" />
        {!collapsed && <span className="truncate text-primary">{name}</span>}
      </MenuTrigger>
    </ShortcutTooltip>

    <MenuContent side="right" align="end" className="w-rail-menu">
      <div className="px-inset pt-2 pb-inset">
        <p className="truncate text-data text-primary">{name}</p>
        <p className="mt-tight truncate font-mono text-micro text-faint">{email}</p>
      </div>

      <MenuSeparator />

      <MenuItem className="text-primary" onSelect={onAccount}>
        <ProfileIcon />
        <span className="text-data">Account</span>
      </MenuItem>
      <MenuItem className="text-primary" onSelect={onShortcuts}>
        <KeyboardIcon />
        <span className="text-data">Keyboard shortcuts</span>
        <span className={ITEM_KEY}>{SHELL_CHORDS.shortcuts.keys}</span>
      </MenuItem>

      <MenuSeparator />

      {/* All three at once, as on main: a cycling item made "which one is System" a guess. */}
      <div className="px-inset pt-label">
        <Segmented fill aria-label="Theme" segments={THEMES} value={theme} onChange={onTheme} />
      </div>
      <p className="truncate px-inset pt-label pb-label font-mono text-micro text-faint">
        Times in {timeZone}
      </p>

      <MenuSeparator />

      <MenuItem className="text-primary" onSelect={onSignOut}>
        <SignOutIcon />
        <span className="text-data">Sign out</span>
      </MenuItem>
    </MenuContent>
  </Menu>
);
